import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Link, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import {
  IngestionBrandInfo,
  IngestionJob,
  ManagedBrandSummary,
  PricingSubmission,
  integrationApiService,
} from '../../services/integrationApi';
import {
  HEALTH_LABELS,
  HEALTH_TONES,
  PRICE_REFRESH_SCHEDULE_LABEL,
  connectorMethodLabel,
  deriveConnectorHealth,
  formatRelativeTime,
  formatScheduleLine,
  formatShortDate,
  isPriceRefreshOn,
} from './connectorHealth';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import {
  cardLabel,
  ellipsis,
  groupHeading,
  ingest,
  primaryButton,
  rowButton,
  secondaryButton,
  tabular,
} from './ingestionTokens';
import { tokens } from '../../theme/tokens';
import { BrandMarkTile } from './BrandMarkTile';
import { IngestorCoveragePanel } from './IngestorCoveragePanel';
import { RunHistoryChart } from './RunSparkline';
import { RunLogPanel } from './RunLogPanel';
import { RunFacts, VERDICT_TONES, deriveConnectorAlert, isTerminalVerdict, runFacts } from './runFacts';
import { classifyRunOutcome } from './runOutcome';
import { ForceStartConfirm } from './ForceStartConfirm';

export type ConnectorPanel = 'overview' | 'runs' | 'categories' | 'prices' | 'rebates';

const OPEN_SIGNAL_STATES = ['new', 'watching', 'scrape_queued'];

interface SignalRow {
  id: string;
  brandId?: string;
  title?: string;
  status?: string;
  articleUrl?: string;
  productUrl?: string;
}

interface ConnectorDetailProps {
  brand: IngestionBrandInfo;
  panel: ConnectorPanel;
  expandedJobId?: string | null;
  onPanelChange: (panel: ConnectorPanel) => void;
  onConfigure: () => void;
  onRunNow: () => void;
  /** Admin force start; shown when a run is blocked because something reads as running. */
  onForceStart?: () => void;
  /** True when Run now is disabled only because a run reads as running (not coming soon, not in flight). */
  forceStartAvailable?: boolean;
  forceStartPending?: boolean;
  onPriceRefresh?: () => void;
  onViewLog: (jobId: string) => void;
  runDisabled?: boolean;
  priceRefreshDisabled?: boolean;
  priceRefreshPending?: boolean;
  runningBrandName?: string;
}

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

export const ConnectorDetail: React.FC<ConnectorDetailProps> = ({
  brand,
  panel,
  expandedJobId,
  onPanelChange,
  onConfigure,
  onRunNow,
  onForceStart,
  forceStartAvailable,
  forceStartPending,
  onPriceRefresh,
  onViewLog,
  runDisabled,
  priceRefreshDisabled,
  priceRefreshPending,
  runningBrandName,
}) => {
  const health = deriveConnectorHealth(brand);
  const tone = HEALTH_TONES[health];
  const [forceConfirmOpen, setForceConfirmOpen] = useState(false);

  const { data: jobs = [] } = useQuery({
    queryKey: ['ingestion-jobs', brand.id],
    queryFn: () => integrationApiService.getIngestionJobs(brand.id, 60),
    refetchInterval: 8000,
  });

  const { data: announcements = [] } = useQuery({
    queryKey: ['announcement-candidates'],
    queryFn: () => integrationApiService.fetchAnnouncementCandidates(undefined, 80),
  });

  const catalogSize = brand.stats?.productCount ?? 0;

  /** One record per run — the history rows, the last-run card and the alert all read these. */
  const runs: RunFacts[] = useMemo(
    () => (jobs as IngestionJob[]).map((job) => runFacts(job, { catalogSize })),
    [jobs, catalogSize]
  );

  // `brand.lastRun` is the last *finished* run; `runs[0]` is whatever is newest,
  // which is a running retry the moment an operator retries a failing connector.
  // Resolving by id keeps the health badge, the last-run card, the alert and its
  // "View log" target all describing the same execution.
  const lastFinishedRun =
    (brand.lastRun ? runs.find((run) => run.id === brand.lastRun?.id) : null) ||
    runs.find((run) => isTerminalVerdict(run.verdict)) ||
    null;
  const runningRun = runs.find((run) => run.verdict === 'Running') || null;
  const alert = deriveConnectorAlert(brand, lastFinishedRun);

  const brandSignals = (announcements as SignalRow[]).filter(
    (signal) => signal.brandId === brand.id && OPEN_SIGNAL_STATES.includes(String(signal.status || ''))
  );

  // Terminal runs only, so the caption counts exactly what the chart draws.
  const windowRuns = useMemo(
    () =>
      runs.filter((run) => {
        if (!isTerminalVerdict(run.verdict)) return false;
        if (!run.startedAt) return false;
        const started = new Date(run.startedAt).getTime();
        return Number.isFinite(started) && Date.now() - started <= THIRTY_DAYS_MS;
      }),
    [runs]
  );
  const windowFailed = windowRuns.filter((run) => run.verdict === 'Failed').length;
  const isApiSourced = brand.scraperType === 'api' || brand.scraperType === 'feed';

  const subTabs: Array<{ id: ConnectorPanel; label: string; count?: number }> = [
    { id: 'overview', label: 'Overview' },
    { id: 'runs', label: isApiSourced ? 'API ingestion runs' : 'Runs & logs', count: runs.length },
    { id: 'categories', label: 'Categories', count: brand.categories.length },
    { id: 'prices', label: 'Prices' },
    { id: 'rebates', label: 'Rebate & MAP files' },
  ];

  const blockedNote = brand.scraperStatus === 'coming_soon'
    ? 'This connector is not built yet — runs are disabled.'
    : runDisabled && runningBrandName && !brand.isRunning
      ? `An ingestion job is running for ${runningBrandName}.`
      : '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, background: ingest.color.surface }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '14px',
          padding: '18px 24px 14px',
          borderBottom: `1px solid ${ingest.color.borderInner}`,
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
        }}
      >
        <BrandMarkTile brand={brand.name} />
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap' }}>
            <Box
              sx={{
                fontFamily: ingest.font.sans,
                fontSize: '19px',
                fontWeight: 600,
                color: ingest.color.ink,
                letterSpacing: '-0.01em',
              }}
            >
              {brand.name}
            </Box>
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                height: 22,
                padding: '0 9px',
                border: `1px solid ${tone.border}`,
                background: tone.bg,
                borderRadius: '11px',
                fontFamily: ingest.font.sans,
                fontSize: '11.5px',
                fontWeight: 500,
                color: tone.ink,
              }}
            >
              <Box component="span" sx={{ width: '6px', height: '6px', borderRadius: '4px', background: tone.dot }} />
              {HEALTH_LABELS[health]}
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginTop: '5px',
              fontFamily: ingest.font.sans,
              fontSize: '12px',
              color: ingest.color.faint,
              flexWrap: 'wrap',
              ...tabular,
            }}
          >
            <span>{connectorMethodLabel(brand)}</span>
            <Box component="span" sx={{ color: ingest.color.control }}>·</Box>
            <span>{catalogSize.toLocaleString()} products</span>
            <Box component="span" sx={{ color: ingest.color.control }}>·</Box>
            <span>{brand.categories.length} categories mapped</span>
          </Box>
        </Box>
        <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <Button disableElevation sx={secondaryButton} onClick={onConfigure}>
            Configure
          </Button>
          <Tooltip title={blockedNote}>
            <span>
              <Button disableElevation sx={primaryButton} onClick={onRunNow} disabled={runDisabled}>
                ▶ Run now
              </Button>
            </span>
          </Tooltip>
          {forceStartAvailable && onForceStart && (
            <Button
              disableElevation
              sx={secondaryButton}
              onClick={() => setForceConfirmOpen(true)}
              disabled={forceStartPending}
            >
              Force start
            </Button>
          )}
        </Box>
      </Box>

      <ForceStartConfirm
        open={forceConfirmOpen}
        targetName={brand.name}
        runningBrandName={brand.name}
        runningSince={brand.runningSince}
        loading={forceStartPending}
        onClose={() => setForceConfirmOpen(false)}
        onConfirm={() => {
          setForceConfirmOpen(false);
          onForceStart?.();
        }}
      />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '0 24px',
          borderBottom: `1px solid ${ingest.color.borderInner}`,
          overflowX: 'auto',
        }}
      >
        {subTabs.map((tab) => {
          const active = panel === tab.id;
          return (
            <Box
              key={tab.id}
              component="button"
              type="button"
              aria-current={active ? 'page' : undefined}
              onClick={() => onPanelChange(tab.id)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                height: 36,
                padding: '0 11px',
                border: 0,
                borderBottom: `2px solid ${active ? ingest.color.accent : 'transparent'}`,
                background: 'transparent',
                fontFamily: ingest.font.sans,
                fontSize: '12.5px',
                fontWeight: active ? 600 : 500,
                color: active ? ingest.color.ink : ingest.color.muted,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                '&:hover': { color: ingest.color.ink },
              }}
            >
              <span>{tab.label}</span>
              {tab.count !== undefined && (
                <Box component="span" sx={{ ...tabular, fontSize: '11px', color: ingest.color.faintAlt }}>
                  {tab.count}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, overflowY: 'auto' }}>
        {panel === 'overview' && (
          <OverviewPanel
            brand={brand}
            runs={windowRuns}
            lastFinishedRun={lastFinishedRun}
            runningRun={runningRun}
            alert={alert}
            windowRunCount={windowRuns.length}
            windowFailedCount={windowFailed}
            signals={brandSignals}
            onViewLog={onViewLog}
            isApiSourced={isApiSourced}
          />
        )}
        {panel === 'runs' && <RunsPanel runs={runs} expandedJobId={expandedJobId} />}
        {panel === 'categories' && <CategoriesPanel brand={brand} isApiSourced={isApiSourced} />}
        {panel === 'prices' && (
          <PricesPanel
            brand={brand}
            onPriceRefresh={onPriceRefresh}
            priceRefreshDisabled={priceRefreshDisabled}
            priceRefreshPending={priceRefreshPending}
          />
        )}
        {panel === 'rebates' && <RebateFilesPanel brand={brand} />}
      </Box>
    </Box>
  );
};

const SectionHeading: React.FC<{ title: string; note?: string }> = ({ title, note }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
    <Box sx={{ fontFamily: ingest.font.sans, fontSize: '13px', fontWeight: 600, color: ingest.color.ink }}>
      {title}
    </Box>
    {note && (
      <Box sx={{ fontFamily: ingest.font.sans, fontSize: '12px', color: ingest.color.faint, ...tabular }}>{note}</Box>
    )}
  </Box>
);

const ScheduleCard: React.FC<{ label: string; value: string; meta: string; valueInk?: string }> = ({
  label,
  value,
  meta,
  valueInk,
}) => (
  <Box sx={{ border: `1px solid ${ingest.color.borderInner}`, borderRadius: '9px', padding: '13px 15px' }}>
    <Box sx={cardLabel}>{label}</Box>
    <Box
      sx={{
        fontFamily: ingest.font.sans,
        fontSize: '14px',
        color: valueInk || ingest.color.ink,
        marginTop: '7px',
      }}
    >
      {value}
    </Box>
    <Box
      sx={{ fontFamily: ingest.font.sans, fontSize: '12px', color: ingest.color.faint, marginTop: '3px', ...tabular }}
    >
      {meta}
    </Box>
  </Box>
);

/**
 * MOT-767: the brand's site refuses Chrome, so the schedulers hold every browser
 * run and the daily price refresh probes at `nextProbeAt`. Replaces the generic
 * "run failed" alert, whose failures are this block; Run now in the header is
 * the manual try, and its outcome clears or extends the hold.
 */
function SiteBlockBanner({
  brand,
  onViewLog,
}: {
  brand: IngestionBrandInfo;
  onViewLog: (jobId: string) => void;
}) {
  const { t } = useAppTranslation();
  const block = brand.siteBlock;
  if (!block) return null;
  const host = block.host || t('admin.dataIngestion.siteBlock.hostFallback');
  return (
    <Box
      role="status"
      sx={{
        margin: '16px 24px 0',
        border: `1px solid ${tokens.signal.caution.border}`,
        background: ingest.color.warningTint,
        borderRadius: '9px',
        padding: '13px 15px',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <Box component="span" aria-hidden sx={{ fontSize: '12px', color: ingest.color.danger }}>
          ▲
        </Box>
        <Box sx={{ fontFamily: ingest.font.sans, fontSize: '12.5px', fontWeight: 600, color: ingest.color.warningInk }}>
          {t('admin.dataIngestion.siteBlock.title', { host, since: formatShortDate(block.since) })}
        </Box>
        <Box sx={{ fontFamily: ingest.font.sans, fontSize: '11.5px', color: ingest.color.warningInk, ...tabular }}>
          {block.probeDue
            ? t('admin.dataIngestion.siteBlock.metaProbeDue')
            : t('admin.dataIngestion.siteBlock.meta', { nextTry: formatShortDate(block.nextProbeAt) })}
        </Box>
        {block.lastJobId && (
          <Button
            disableElevation
            onClick={() => onViewLog(block.lastJobId as string)}
            sx={{
              ...rowButton,
              marginLeft: 'auto',
              border: `1px solid ${tokens.signal.caution.border}`,
              color: ingest.color.warningInk,
            }}
          >
            {t('admin.dataIngestion.siteBlock.viewLog')}
          </Button>
        )}
      </Box>
      <Box
        sx={{
          fontFamily: ingest.font.sans,
          fontSize: '12px',
          color: ingest.color.warningInk,
          marginTop: '7px',
          overflowWrap: 'anywhere',
        }}
      >
        {t('admin.dataIngestion.siteBlock.body', { host, count: block.probeCount + 1 })}
      </Box>
    </Box>
  );
}

function OverviewPanel({
  brand,
  runs,
  lastFinishedRun,
  runningRun,
  alert,
  windowRunCount,
  windowFailedCount,
  signals,
  onViewLog,
  isApiSourced,
}: {
  brand: IngestionBrandInfo;
  runs: RunFacts[];
  lastFinishedRun: RunFacts | null;
  runningRun: RunFacts | null;
  alert: ReturnType<typeof deriveConnectorAlert>;
  windowRunCount: number;
  windowFailedCount: number;
  signals: SignalRow[];
  onViewLog: (jobId: string) => void;
  isApiSourced: boolean;
}) {
  const queryClient = useQueryClient();
  const scrape = useMutation({
    mutationFn: (id: string) => integrationApiService.scrapeAnnouncementCandidate(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['announcement-candidates'] }),
  });
  const scheduleFrequency = useMutation({
    mutationFn: (frequency: 'daily' | 'weekly' | 'monthly') =>
      integrationApiService.updateConnectorSchedule(brand.apiConnectorId as string, frequency),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ingestion-brands'] }),
  });

  const catalogSize = brand.stats?.productCount ?? 0;
  const nextRun = brand.schedule?.nextScheduledRun
    ? new Date(brand.schedule.nextScheduledRun).toLocaleDateString()
    : '—';
  const lastPriceRefresh = runs.find((run) => run.kind === 'Price Refresh');
  const priceRefreshOn = isPriceRefreshOn(brand.schedule);

  // The last-run card reads the same finished record the alert and "View log"
  // use. A newer retry is reported alongside it rather than overwriting it —
  // otherwise the card says Running while the health badge still says Failing.
  const lastLabel = lastFinishedRun
    ? lastFinishedRun.verdict
    : brand.lastRun
      ? classifyRunOutcome(brand.lastRun) === 'failed' ? 'Failed' : 'Completed'
      : 'Never run';
  const lastMeta = [
    lastFinishedRun
      ? `${lastFinishedRun.relative} · ${lastFinishedRun.duration}`
      : brand.lastRun
        ? formatRelativeTime(brand.lastRun.completedAt)
        : '—',
    runningRun ? 'retry in progress' : '',
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Box>
      {brand.siteBlock && <SiteBlockBanner brand={brand} onViewLog={onViewLog} />}
      {alert && !brand.siteBlock && (
        <Box
          sx={{
            margin: '16px 24px 0',
            border: `1px solid ${ingest.color.dangerBorder}`,
            background: ingest.color.dangerTintSoft,
            borderRadius: '9px',
            padding: '13px 15px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <Box component="span" aria-hidden sx={{ fontSize: '12px', color: ingest.color.dangerGlyph }}>
              ▲
            </Box>
            <Box
              sx={{ fontFamily: ingest.font.sans, fontSize: '12.5px', fontWeight: 600, color: ingest.color.dangerInk }}
            >
              {alert.title}
            </Box>
            <Box sx={{ fontFamily: ingest.font.sans, fontSize: '11.5px', color: ingest.color.dangerMeta }}>
              {alert.when}
            </Box>
            {alert.runId && (
              <Button
                disableElevation
                onClick={() => onViewLog(alert.runId as string)}
                sx={{
                  ...rowButton,
                  marginLeft: 'auto',
                  border: `1px solid ${ingest.color.dangerControl}`,
                  color: ingest.color.dangerInk,
                  '&:hover': {
                    background: ingest.color.surface,
                    borderColor: ingest.color.dangerGlyph,
                    color: ingest.color.dangerInk,
                  },
                }}
              >
                View log
              </Button>
            )}
          </Box>
          <Box
            sx={{
              fontFamily: ingest.font.mono,
              fontSize: '12px',
              color: ingest.color.dangerBody,
              marginTop: '7px',
              overflowWrap: 'anywhere',
            }}
          >
            {alert.body}
          </Box>
        </Box>
      )}

      <Box
        sx={{
          padding: '16px 24px',
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(auto-fit, minmax(210px, 1fr))' },
          gap: '14px',
        }}
      >
        <Box sx={{ border: `1px solid ${ingest.color.borderInner}`, borderRadius: '9px', padding: '13px 15px' }}>
          <Box sx={cardLabel}>{isApiSourced ? 'API ingestion' : 'Full scrape'}</Box>
          <Box sx={{ fontFamily: ingest.font.sans, fontSize: '14px', color: ingest.color.ink, marginTop: '7px' }}>
            {formatScheduleLine(brand)}
          </Box>
          <Box sx={{ fontFamily: ingest.font.sans, fontSize: '12px', color: ingest.color.faint, marginTop: '3px', ...tabular }}>
            Next {nextRun}
          </Box>
          {isApiSourced && brand.apiConnectorId && (
            <Select
              size="small"
              value={brand.schedule?.frequency && brand.schedule.frequency !== 'manual' ? brand.schedule.frequency : 'weekly'}
              onChange={(event) =>
                scheduleFrequency.mutate(event.target.value as 'daily' | 'weekly' | 'monthly')
              }
              disabled={scheduleFrequency.isPending}
              inputProps={{ 'aria-label': 'API ingestion frequency' }}
              sx={{ marginTop: '9px', fontSize: '12.5px', height: 30, width: '100%' }}
            >
              <MenuItem value="daily" sx={{ fontSize: '12.5px' }}>Daily</MenuItem>
              <MenuItem value="weekly" sx={{ fontSize: '12.5px' }}>Weekly</MenuItem>
              <MenuItem value="monthly" sx={{ fontSize: '12.5px' }}>Monthly</MenuItem>
            </Select>
          )}
        </Box>
        {!isApiSourced && (
          <ScheduleCard
            label="Price refresh"
            value={priceRefreshOn ? PRICE_REFRESH_SCHEDULE_LABEL : 'Paused'}
            meta={lastPriceRefresh ? `ran ${lastPriceRefresh.relative}` : 'no recent price refresh'}
          />
        )}
        <ScheduleCard
          label="Last run"
          value={lastLabel}
          valueInk={lastLabel === 'Failed' ? ingest.color.dangerInk : ingest.color.ink}
          meta={lastMeta}
        />
      </Box>

      <IngestorCoveragePanel brandId={brand.id} />

      <Box sx={{ padding: '0 24px 18px' }}>
        <SectionHeading
          title="Last 30 days"
          note={`${windowRunCount} runs · ${windowFailedCount} failed · ${catalogSize.toLocaleString()} products in catalog`}
        />
        <RunHistoryChart runs={runs} onSelectRun={onViewLog} />
      </Box>

      <Box sx={{ padding: '0 24px 22px' }}>
        <SectionHeading
          title="Signals for this brand"
          note={signals.length ? `${signals.length} open · from the watchlist` : ''}
        />
        <Box sx={{ border: `1px solid ${ingest.color.borderInner}`, borderRadius: '9px', overflow: 'hidden' }}>
          {signals.length === 0 ? (
            <Box
              sx={{ padding: '14px', fontFamily: ingest.font.sans, fontSize: '12.5px', color: ingest.color.faintAlt }}
            >
              No open signals for {brand.name}.
            </Box>
          ) : (
            signals.map((signal) => {
              const queued = signal.status === 'scrape_queued';
              return (
                <Box
                  key={signal.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '10px 14px',
                    borderBottom: `1px solid ${ingest.color.borderFaintAlt}`,
                    '&:last-of-type': { borderBottom: 0 },
                    '&:hover': { background: ingest.color.sunken },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      height: 20,
                      padding: '0 8px',
                      borderRadius: '10px',
                      background: queued ? ingest.color.queuedTint : ingest.color.accentTint,
                      color: queued ? ingest.color.queuedInk : ingest.color.accentHover,
                      fontFamily: ingest.font.sans,
                      fontSize: '11px',
                      fontWeight: 500,
                      lineHeight: '20px',
                      flexShrink: 0,
                    }}
                  >
                    {signal.status || 'watching'}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Link
                      href={signal.articleUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{
                        display: 'block',
                        fontFamily: ingest.font.sans,
                        fontSize: '12.5px',
                        color: ingest.color.accent,
                        ...ellipsis,
                      }}
                    >
                      {signal.title}
                    </Link>
                  </Box>
                  <Button
                    disableElevation
                    sx={{ ...rowButton, flexShrink: 0 }}
                    disabled={!signal.productUrl || scrape.isPending}
                    onClick={() => scrape.mutate(signal.id)}
                  >
                    Scrape
                  </Button>
                </Box>
              );
            })
          )}
        </Box>
      </Box>
    </Box>
  );
}

function RunsPanel({ runs, expandedJobId }: { runs: RunFacts[]; expandedJobId?: string | null }) {
  const [openId, setOpenId] = useState<string | null>(expandedJobId || null);

  useEffect(() => {
    if (expandedJobId) setOpenId(expandedJobId);
  }, [expandedJobId]);

  useEffect(() => {
    if (!openId) return;
    document.getElementById(`ingestion-run-${openId}`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [openId]);

  if (runs.length === 0) {
    return (
      <Box sx={{ padding: '18px 24px', fontFamily: ingest.font.sans, fontSize: '12.5px', color: ingest.color.faintAlt }}>
        No runs recorded for this connector yet.
      </Box>
    );
  }

  // Two content columns, not four: the pane is too narrow for a wide table, so
  // status keeps a fixed track and everything else stacks in one fluid column.
  // Below `md` the row becomes a single stacked column (see useIsMobile / the
  // mobile layout guard in docs/internal/MOBILE_UI_ASSESSMENT.md).
  const columns = { xs: 'minmax(0,1fr)', md: '96px minmax(0, 1fr) 30px' };

  return (
    <Box sx={{ padding: '16px 24px 24px' }}>
      <Box
        role="grid"
        aria-label="Run history"
        sx={{ border: `1px solid ${ingest.color.borderInner}`, borderRadius: '9px', overflow: 'hidden' }}
      >
        <Box
          role="row"
          sx={{
            display: { xs: 'none', md: 'grid' },
            gridTemplateColumns: columns,
            gap: '10px',
            padding: '9px 14px',
            background: ingest.color.sunken,
            borderBottom: `1px solid ${ingest.color.borderInner}`,
            ...groupHeading,
            letterSpacing: '0.07em',
            color: ingest.color.faintAlt,
          }}
        >
          <Box role="columnheader">Status</Box>
          <Box role="columnheader">Run</Box>
          <Box role="columnheader" aria-label="Expand" />
        </Box>

        {runs.map((run) => {
          const open = openId === run.id;
          const verdictTone = VERDICT_TONES[run.verdict];
          return (
            <Box key={run.id} id={`ingestion-run-${run.id}`}>
              <Box
                role="row"
                tabIndex={0}
                aria-expanded={open}
                aria-label={`${run.verdict}. ${run.result}. ${run.meta}`}
                onClick={() => setOpenId(open ? null : run.id)}
                onKeyDown={(event: React.KeyboardEvent) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return;
                  event.preventDefault();
                  setOpenId(open ? null : run.id);
                }}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: columns,
                  gap: '10px',
                  alignItems: 'center',
                  width: '100%',
                  padding: '10px 14px',
                  border: 0,
                  borderBottom: `1px solid ${ingest.color.borderFaintAlt}`,
                  background: open ? ingest.color.rowOpen : ingest.color.surface,
                  cursor: 'pointer',
                  textAlign: 'left',
                  '&:hover': { background: open ? ingest.color.rowOpen : ingest.color.sunken },
                  '&:focus-visible': { outline: `2px solid ${ingest.color.accent}`, outlineOffset: '-2px' },
                }}
              >
                <Box component="span" role="gridcell">
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-block',
                      height: 20,
                      padding: '0 8px',
                      borderRadius: '10px',
                      background: verdictTone.bg,
                      color: verdictTone.ink,
                      fontFamily: ingest.font.sans,
                      fontSize: '11.5px',
                      fontWeight: 500,
                      lineHeight: '20px',
                    }}
                  >
                    {run.verdict}
                  </Box>
                </Box>
                <Box component="span" role="gridcell" sx={{ minWidth: 0 }}>
                  <Box
                    component="span"
                    sx={{
                      display: 'block',
                      fontFamily: ingest.font.sans,
                      fontSize: '12.5px',
                      color: ingest.color.body,
                      ...ellipsis,
                    }}
                  >
                    {run.result}
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      display: 'block',
                      fontFamily: ingest.font.sans,
                      fontSize: '11px',
                      color: ingest.color.faintAlt,
                      marginTop: '2px',
                      ...tabular,
                      ...ellipsis,
                    }}
                  >
                    {run.meta}
                  </Box>
                </Box>
                <Box
                  component="span"
                  role="gridcell"
                  aria-hidden
                  sx={{ fontSize: '11px', color: ingest.color.faintAlt, textAlign: 'center' }}
                >
                  {open ? '▾' : '▸'}
                </Box>
              </Box>
              {open && <RunLogPanel logs={run.logs} actor={run.actor} />}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function CategoriesPanel({ brand, isApiSourced }: { brand: IngestionBrandInfo; isApiSourced: boolean }) {
  return (
    <Box sx={{ padding: '18px 24px 24px' }}>
      <Box
        sx={{
          fontFamily: ingest.font.sans,
          fontSize: '12.5px',
          color: ingest.color.faint,
          marginBottom: '12px',
        }}
      >
        {isApiSourced ? (
          `${brand.categories.length} categories mapped from the vendor API feed.`
        ) : (
          <>
            {brand.categories.length} categories mapped · unmapped products land in{' '}
            <Link
              component={RouterLink}
              to={`/app/admin/staged-products?brand=${encodeURIComponent(brand.id)}`}
              underline="hover"
              sx={{ color: ingest.color.accent }}
            >
              Staged Products
            </Link>
            .
          </>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '7px' }}>
        {brand.categories.length === 0 ? (
          <Box sx={{ fontFamily: ingest.font.sans, fontSize: '12.5px', color: ingest.color.faintAlt }}>
            No categories mapped for this connector.
          </Box>
        ) : (
          brand.categories.map((category) => (
            <Box
              key={category}
              component="span"
              sx={{
                height: 28,
                padding: '0 11px',
                border: `1px solid ${tokens.signal.neutral.border}`,
                background: ingest.color.sunken,
                borderRadius: '14px',
                fontFamily: ingest.font.sans,
                fontSize: '12.5px',
                color: ingest.color.bodyAlt,
                lineHeight: '27px',
              }}
            >
              {category}
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
}

const PriceCard: React.FC<{
  label: string;
  value: React.ReactNode;
  meta: string;
  href: string;
  valueInk?: string;
}> = ({ label, value, meta, href, valueInk }) => (
  <Box
    component={RouterLink}
    to={href}
    sx={{
      border: `1px solid ${ingest.color.borderInner}`,
      borderRadius: '9px',
      padding: '13px 15px',
      textDecoration: 'none',
      display: 'block',
      '&:hover': { borderColor: ingest.color.accent },
    }}
  >
    <Box sx={cardLabel}>{label}</Box>
    <Box
      sx={{
        fontFamily: ingest.font.sans,
        fontSize: '22px',
        fontWeight: 600,
        color: valueInk || ingest.color.ink,
        marginTop: '7px',
        ...tabular,
      }}
    >
      {value}
    </Box>
    <Box sx={{ fontFamily: ingest.font.sans, fontSize: '12px', color: ingest.color.faint, marginTop: '3px' }}>
      {meta}
    </Box>
  </Box>
);

function PricesPanel({
  brand,
  onPriceRefresh,
  priceRefreshDisabled,
  priceRefreshPending,
}: {
  brand: IngestionBrandInfo;
  onPriceRefresh?: () => void;
  priceRefreshDisabled?: boolean;
  priceRefreshPending?: boolean;
}) {
  const freshness = useQuery({
    queryKey: ['pricing-freshness', brand.id, 2],
    queryFn: () => integrationApiService.fetchStalePricingProducts({ brandId: brand.id, days: 2, limit: 1 }),
  });
  const changes = useQuery({
    queryKey: ['catalog-price-changes', brand.id, 7],
    queryFn: () => integrationApiService.fetchCatalogPriceChanges({ brandId: brand.id, days: 7, limit: 500 }),
  });

  const scanned = freshness.data?.summary.scanned ?? 0;
  const stale = freshness.data?.summary.stale ?? 0;
  const freshPct = scanned > 0 ? `${Math.round(((scanned - stale) / scanned) * 100)}%` : '—';
  const changeCount = changes.data?.count ?? 0;
  const spinner = <CircularProgress size={18} sx={{ color: ingest.color.faintAlt }} />;

  return (
    <Box sx={{ padding: '16px 24px 24px' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(auto-fit, minmax(210px, 1fr))' },
          gap: '14px',
        }}
      >
        <PriceCard
          label="Freshness"
          value={freshness.isLoading ? spinner : freshPct}
          meta="priced within 48h"
          href={`/app/admin/pricing-freshness?brand=${encodeURIComponent(brand.id)}&days=2`}
        />
        <PriceCard
          label="Changes · 7d"
          value={changes.isLoading ? spinner : changeCount}
          meta={`across ${(brand.stats?.productCount ?? 0).toLocaleString()} products`}
          href={`/app/admin/catalog-price-changes?brand=${encodeURIComponent(brand.id)}`}
        />
        <PriceCard
          label="MAP violations"
          value="—"
          meta="no open-count endpoint yet"
          href="/app/admin/map-pricing"
        />
      </Box>
      {onPriceRefresh && (
        <Box sx={{ marginTop: '14px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <Button
            disableElevation
            sx={secondaryButton}
            onClick={onPriceRefresh}
            disabled={priceRefreshDisabled || priceRefreshPending}
            startIcon={priceRefreshPending ? <CircularProgress size={13} /> : undefined}
          >
            Refresh prices now
          </Button>
          <Typography
            sx={{ fontFamily: ingest.font.sans, fontSize: '12px', color: ingest.color.faint }}
          >
            Runs a US price sweep for this connector outside the {PRICE_REFRESH_SCHEDULE_LABEL} schedule.
          </Typography>
        </Box>
      )}
    </Box>
  );
}

const STATUS_TONES: Record<PricingSubmission['status'], { bg: string; ink: string }> = {
  processed: { bg: ingest.color.successTint, ink: ingest.color.successInk },
  approved: { bg: ingest.color.successTint, ink: ingest.color.successInk },
  pending: { bg: tokens.signal.neutral.tint, ink: ingest.color.muted },
  rejected: { bg: ingest.color.dangerTint, ink: ingest.color.dangerInk },
  duplicate: { bg: ingest.color.warningTint, ink: ingest.color.warningInk },
};

function formatDate(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
}

function RebateFilesPanel({ brand }: { brand: IngestionBrandInfo }) {
  const { data: submissions = [], isLoading: submissionsLoading } = useQuery({
    queryKey: ['vendor-pricing-submissions', brand.name],
    queryFn: () => integrationApiService.getVendorPricingSubmissions(brand.name, 25),
  });

  const { data: managedBrands = [], isLoading: brandsLoading } = useQuery({
    queryKey: ['vendor-managed-brands', brand.name],
    queryFn: () => integrationApiService.getVendorManagedBrands(brand.name),
  });

  return (
    <Box sx={{ padding: '18px 24px 24px' }}>
      <SectionHeading
        title="Brands managed"
        note={managedBrands.length ? `${managedBrands.length} brands` : undefined}
      />
      <Box sx={{ border: `1px solid ${ingest.color.borderInner}`, borderRadius: '9px', overflow: 'hidden', marginBottom: '20px' }}>
        {brandsLoading ? (
          <Box sx={{ padding: '14px' }}>
            <CircularProgress size={16} sx={{ color: ingest.color.faintAlt }} />
          </Box>
        ) : managedBrands.length === 0 ? (
          <Box sx={{ padding: '14px', fontFamily: ingest.font.sans, fontSize: '12.5px', color: ingest.color.faintAlt }}>
            No brands found in {brand.name}'s submission history yet.
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '7px', padding: '13px 14px' }}>
            {managedBrands.map((entry: ManagedBrandSummary) => (
              <Box
                key={entry.brand}
                component="span"
                sx={{
                  height: 28,
                  padding: '0 11px',
                  border: `1px solid ${tokens.signal.neutral.border}`,
                  background: ingest.color.sunken,
                  borderRadius: '14px',
                  fontFamily: ingest.font.sans,
                  fontSize: '12.5px',
                  color: ingest.color.bodyAlt,
                  lineHeight: '27px',
                }}
              >
                {entry.brand}
                <Box component="span" sx={{ color: ingest.color.faintAlt, marginLeft: '5px', ...tabular }}>
                  · {entry.fileCount}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <SectionHeading
        title="Rebate & MAP files ingested"
        note={submissions.length ? `${submissions.length} files` : undefined}
      />
      <Box sx={{ border: `1px solid ${ingest.color.borderInner}`, borderRadius: '9px', overflow: 'hidden' }}>
        {submissionsLoading ? (
          <Box sx={{ padding: '14px' }}>
            <CircularProgress size={16} sx={{ color: ingest.color.faintAlt }} />
          </Box>
        ) : submissions.length === 0 ? (
          <Box sx={{ padding: '14px', fontFamily: ingest.font.sans, fontSize: '12.5px', color: ingest.color.faintAlt }}>
            No rebate or MAP files ingested for {brand.name} yet.
          </Box>
        ) : (
          submissions.map((submission: PricingSubmission) => {
            const tone = STATUS_TONES[submission.status] || STATUS_TONES.pending;
            return (
              <Box
                key={submission.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px 14px',
                  borderBottom: `1px solid ${ingest.color.borderFaintAlt}`,
                  '&:last-of-type': { borderBottom: 0 },
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ fontFamily: ingest.font.sans, fontSize: '12.5px', color: ingest.color.ink, ...ellipsis }}>
                    {submission.fileName}
                  </Box>
                  <Box sx={{ fontFamily: ingest.font.sans, fontSize: '11.5px', color: ingest.color.faint, marginTop: '2px' }}>
                    {formatDate(submission.createdAt)}
                    {submission.recordsFound != null ? ` · ${submission.recordsFound} records` : ''}
                    {submission.source ? ` · ${submission.source}` : ''}
                  </Box>
                </Box>
                <Box
                  component="span"
                  sx={{
                    height: 20,
                    padding: '0 8px',
                    borderRadius: '10px',
                    background: tone.bg,
                    color: tone.ink,
                    fontFamily: ingest.font.sans,
                    fontSize: '11px',
                    fontWeight: 500,
                    lineHeight: '20px',
                    flexShrink: 0,
                    textTransform: 'capitalize',
                  }}
                >
                  {submission.status}
                </Box>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
}

export default ConnectorDetail;
